import axios from 'axios'
import Button from 'ext-ui/components/Button/Button'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router'
import { AnalyticsPageName } from 'src/analytics/analytics.models'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import * as style from 'src/app/CardFeedDetails/CardFeedDetails.style'
import { CardholderSelection } from 'src/app/CardFeedDetails/components/AssignCardholderDropdown'
import CardFeedCardsTable from 'src/app/CardFeedDetails/components/CardFeedCardsTable'
import CardFeedDetailsHeaderTitle from 'src/app/CardFeedDetails/components/CardFeedDetailsHeaderTitle'
import { toRecipient } from 'src/app/CardFeedDetails/utils/toRecipient'
import AssignAccountOwnerFlyout from 'src/component-flyout/AssignAccountOwnerFlyout/AssignAccountOwnerFlyout'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import ConfirmCardAssignmentModal, {
  AssignmentRecipient,
} from 'src/component-modal/ConfirmCardAssignmentModal/ConfirmCardAssignmentModal'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import { useCurrentBrand } from 'src/context/ConfigContext/useConfig'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { CardFeedDetailsMatchParams } from 'src/navigation/routeProps'
import {
  cardsPhysicalCardsUrl,
  createCardFeedUrl,
  homeUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { CardFeedCard } from 'src/queries/cardFeeds/cardFeeds.models'
import useQueryCardFeed from 'src/queries/cardFeeds/domain/useQueryCardFeed'
import useQueryCardFeedCards from 'src/queries/cardFeeds/domain/useQueryCardFeedCards'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'

const CardFeedDetails: React.FC = () => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const history = useHistory()
  const { id } = useParams<CardFeedDetailsMatchParams>()

  const { data: featuresData, isLoading: isFeaturesLoading } =
    useQueryUserFeatures([UserFeature.BULK_CREDIT_CARD_ENROLLMENT])
  const bulkCreditCardEnrollmentEnabled =
    featuresData?.BULK_CREDIT_CARD_ENROLLMENT ?? false
  const { enable_card_feeds: cardFeedsEnabled } =
    useSelector(selectFeatureFlaggerFromState) ?? {}

  const isAllowed = bulkCreditCardEnrollmentEnabled && !!cardFeedsEnabled

  const user = useSelector(selectUserListModelFromState)
  const {
    data: feedData,
    isLoading: isFeedLoading,
    isError: isFeedError,
    error: feedError,
  } = useQueryCardFeed(user?.organizationId, id, isAllowed)
  // A submitted (pending) feed has no connected card program yet, so we may not
  // have a real name to show — fall back to a generic "Pending Feed" label.
  const feedName =
    feedData?.status === 'SUBMITTED'
      ? translate('Pending Feed')
      : (feedData?.cardProgramName ?? translate('Card Feed'))

  // A 404 means the feed doesn't exist — route the user into creation so they
  // can add one. Other errors (network, 5xx) fall through to an error state
  // below rather than dumping the user into create on a transient outage.
  const isFeedNotFound =
    isFeedError &&
    axios.isAxiosError(feedError) &&
    feedError.response?.status === 404

  useEffect(() => {
    if (isFeaturesLoading) return
    if (!isAllowed) {
      history.replace(homeUrl())
    }
  }, [isAllowed, isFeaturesLoading, history])

  useEffect(() => {
    if (!isAllowed || !isFeedNotFound) return
    analytics.track('card feed details', { action: 'not found redirect' })
    history.replace(createCardFeedUrl())
  }, [isAllowed, isFeedNotFound, analytics, history])

  useEffect(() => {
    if (!isAllowed) return
    analytics.page(AnalyticsPageName.OrgSettingsCardFeedDetailsTab)
    analytics.track('card feed details', { action: 'viewed' })
  }, [analytics, isAllowed])

  // Hold the gate with a full-page loader while the feature query resolves —
  // otherwise we flash a blank screen before either the page or the redirect.
  if (isFeaturesLoading) {
    return <LoadingBackgroundPage />
  }

  if (!isAllowed || isFeedNotFound) {
    return null
  }

  // Feed query still in flight: show the loader rather than the page chrome
  // with a placeholder title, until we know the real feed name.
  if (isFeedLoading) {
    return <LoadingBackgroundPage />
  }

  // Non-404 errors: show an error state instead of redirecting, so a transient
  // failure doesn't bounce the user into the create flow.
  if (isFeedError) {
    return (
      <>
        <Helmet>
          <title>{translate('Card Feed')}</title>
        </Helmet>
        <LayoutPage title={<span>{feedName}</span>}>
          <div
            role="alert"
            css={style.errorState}
            data-testid="card-feed-details-error"
          >
            {translate(
              'We couldn’t load this card feed. Please refresh the page or try again later.',
            )}
          </div>
        </LayoutPage>
      </>
    )
  }

  // Not loading and not errored, so the feed resolved — the guard also narrows
  // feedData to non-null for the header below.
  if (!feedData) {
    return <LoadingBackgroundPage />
  }

  const title = (
    <CardFeedDetailsHeaderTitle
      name={feedName}
      status={feedData.status}
      referenceId={feedData.referenceId}
    />
  )

  return <CardFeedDetailsLoaded id={id} title={title} />
}

interface CardFeedDetailsLoadedProps {
  id: string
  title: React.ReactElement
}

const CardFeedDetailsLoaded: React.FC<CardFeedDetailsLoadedProps> = ({
  id,
  title,
}) => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  // React Query dedupes — calling the same hook here and inside the table
  // shares the same network request and cache entry. Avoids lifting all the
  // table's UI state up just to read the unassigned set for "Assign All".
  // The outer `if (!isAllowed) return null` already gates whether this
  // component mounts, so no `enabled` flag is needed on the hook.
  const {
    cards: cardsData,
    isLoading: isCardsLoading,
    isError: isCardsError,
  } = useQueryCardFeedCards(id)
  const unassignedCards = cardsData.filter((c) => c.status === 'UNASSIGNED')
  // Assign All is only actionable when we have data and at least one
  // unassigned card. Loading and error states both disable the button so
  // users don't see a permanently-disabled action with no explanation —
  // the table itself surfaces the error/loading row.
  const canAssign =
    !isCardsLoading && !isCardsError && unassignedCards.length > 0

  const [flyoutCards, setFlyoutCards] = useState<CardFeedCard[]>([])
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false)
  const [selectionResetSignal, setSelectionResetSignal] = useState(0)
  // Single-row dropdown picks bypass the flyout and open the confirm modal
  // directly — the recipient was already chosen inline in the row.
  const [confirmState, setConfirmState] = useState<{
    cards: CardFeedCard[]
    recipient: AssignmentRecipient
  } | null>(null)

  const openFlyoutWith = (
    nextCards: CardFeedCard[],
    source: 'assign all' | 'snackbar',
  ): void => {
    if (nextCards.length === 0) return
    setFlyoutCards(nextCards)
    setIsFlyoutOpen(true)
    analytics.track('card feed details', {
      action: 'assign flyout opened',
      source,
      count: `${nextCards.length}`,
    })
  }

  const handleAssignAllClick = (): void => {
    openFlyoutWith(unassignedCards, 'assign all')
  }

  const handleCloseFlyout = (): void => {
    setIsFlyoutOpen(false)
  }

  const handleAssignFromRow = (
    card: CardFeedCard,
    selection: CardholderSelection,
  ): void => {
    setConfirmState({ cards: [card], recipient: toRecipient(selection) })
    analytics.track('card feed details', {
      action: 'assign confirm opened',
      source: 'row dropdown',
      count: '1',
    })
  }

  const handleAssigned = (): void => {
    // The assigned cards flip to ASSIGNED, so clear the table's selection.
    setSelectionResetSignal((signal) => signal + 1)
    setIsFlyoutOpen(false)
    setConfirmState(null)
  }

  const actions = (
    <Button
      variant="primary"
      onClick={handleAssignAllClick}
      disabled={!canAssign}
      data-testid="card-feed-details-assign-all"
    >
      {translate('Assign All')}
    </Button>
  )

  return (
    <>
      <Helmet>
        <title>{translate('Card Feed')}</title>
      </Helmet>
      <LayoutPage title={title} subtitle={<Subtitle />} actions={actions}>
        <CardFeedCardsTable
          feedId={id}
          onAssignRequest={(c): void => openFlyoutWith(c, 'snackbar')}
          onAssignFromRow={handleAssignFromRow}
          resetSelectionSignal={selectionResetSignal}
        />
      </LayoutPage>
      <AssignAccountOwnerFlyout
        isOpen={isFlyoutOpen}
        onClose={handleCloseFlyout}
        cards={flyoutCards}
        cardFeedId={id}
        onAssigned={handleAssigned}
      />
      {confirmState && (
        <ConfirmCardAssignmentModal
          isOpen
          onClose={(): void => setConfirmState(null)}
          recipient={confirmState.recipient}
          cards={confirmState.cards}
          cardFeedId={id}
          onAssigned={handleAssigned}
        />
      )}
    </>
  )
}

const Subtitle: React.FC = () => {
  const translate = useTranslate()
  const { APP_NAME: rawAppName } = useCurrentBrand()
  return (
    <span css={style.subtitle}>
      {translate(
        'Active cards will appear in your ${cardsPageLink} once card holders have been assigned to your Org in ${appName}.',
        {
          cardsPageLink: (
            <HoverableLink
              to={cardsPhysicalCardsUrl()}
              regular
              underline
              size={16}
              ariaLabelSource="cards page"
              testId="card-feed-details-cards-page-link"
            >
              {translate('cards page')}
            </HoverableLink>
          ),
          appName: translate(rawAppName),
        },
      )}
    </span>
  )
}

export default CardFeedDetails
